import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Spinner, Alert, Modal, StatusBadge, PasswordInput } from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmProvider';

const emptyForm = {
  name: '', employee_id: '', mobile: '', team_lead: '', head_quarter: '',
  password: '', role: 'user', is_active: true,
};

export default function Users() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // {id?, ...form}
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { load(); }, []);
  async function load() {
    try { setUsers(await api.get('/users')); } catch (e) { setError(e.message); }
  }

  function openCreate() { setModal({ ...emptyForm }); }
  function openEdit(u) {
    setModal({
      id: u.id, name: u.name, employee_id: u.employee_id || '', mobile: u.mobile,
      team_lead: u.team_lead || '', head_quarter: u.head_quarter || '',
      role: u.role, is_active: u.is_active, password: '',
    });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...modal };
      if (!payload.password) delete payload.password;
      const saved = modal.id
        ? await api.put(`/users/${modal.id}`, payload)
        : await api.post('/users', payload);
      // Update the list from the response instead of refetching (one round trip).
      setUsers(prev => {
        const list = prev || [];
        return modal.id ? list.map(u => (u.id === saved.id ? saved : u)) : [saved, ...list];
      });
      setModal(null);
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  // Accounts are never deleted - deactivating keeps the exam history intact and
  // lets the same person be switched back on later.
  async function toggleActive(u) {
    const turningOff = u.is_active;
    const msg = turningOff
      ? `Deactivate "${u.name}"? They will not be able to log in until you activate them again. Their exam history is kept.`
      : `Activate "${u.name}"? They will be able to log in and take exams again.`;
    if (!(await confirm({ title: turningOff ? 'Deactivate user' : 'Activate user', message: msg, confirmText: turningOff ? 'Deactivate' : 'Activate', danger: turningOff }))) return;
    setBusyId(u.id); setError('');
    try {
      const saved = await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      setUsers(prev => (prev || []).map(x => (x.id === saved.id ? saved : x)));
    } catch (e) { setError(e.message); }
    setBusyId(null);
  }

  if (!users && !error) return <Spinner />;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Users</h1>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>Add user</button>
      </div>

      <Alert type="danger">{error}</Alert>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Employee ID</th><th>Mobile</th><th>Team lead</th><th>Head quarter</th>
              <th>Role</th><th>Status</th><th>Exams taken</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map(u => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td className="mono">{u.employee_id}</td>
                <td className="mono">{u.mobile}</td>
                <td>{u.team_lead || <span className="text-muted">&mdash;</span>}</td>
                <td>{u.head_quarter || <span className="text-muted">&mdash;</span>}</td>
                <td><StatusBadge status={u.role} /></td>
                <td>{u.is_active ? <StatusBadge status="active" /> : <StatusBadge status="In Active" />}</td>
                <td className="mono">{u.exams_taken ?? 0}</td>
                <td className="table-actions">
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(u)}>Edit</button>
                  {u.id !== me?.id && (
                    <button
                      className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-primary'}`}
                      disabled={busyId === u.id}
                      onClick={() => toggleActive(u)}
                    >
                      {busyId === u.id ? 'Saving…' : u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.id ? 'Edit user' : 'Add user'} onClose={() => setModal(null)}>
          <form onSubmit={save}>
            <div className="form-group">
              <label>Full name</label>
              <input type="text" value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value })} required autoFocus />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Employee ID</label>
                <input
                  type="text" maxLength={30} value={modal.employee_id}
                  onChange={e => setModal({ ...modal, employee_id: e.target.value })}
                  placeholder="e.g. YH1042" required
                />
                <span className="text-sm text-muted">Used to sign in.</span>
              </div>
              <div className="form-group">
                <label>Mobile number</label>
                <input
                  type="tel" maxLength={10} value={modal.mobile}
                  onChange={e => setModal({ ...modal, mobile: e.target.value.replace(/\D/g, '') })} required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Team lead</label>
                <input
                  type="text" value={modal.team_lead}
                  onChange={e => setModal({ ...modal, team_lead: e.target.value })}
                  placeholder="Reporting team lead (optional)"
                />
              </div>
              <div className="form-group">
                <label>Head quarter</label>
                <input
                  type="text" value={modal.head_quarter}
                  onChange={e => setModal({ ...modal, head_quarter: e.target.value })}
                  placeholder="e.g. Hitec City (optional)"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Role</label>
              <select value={modal.role} onChange={e => setModal({ ...modal, role: e.target.value })}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label>{modal.id ? 'New password (leave blank to keep current)' : 'Password'}</label>
              <PasswordInput
                value={modal.password}
                onChange={e => setModal({ ...modal, password: e.target.value })}
                required={!modal.id} minLength={6}
              />
            </div>
            {modal.id && (
              <div className="checkbox-row form-group">
                <input
                  type="checkbox" id="is_active" checked={modal.is_active}
                  onChange={e => setModal({ ...modal, is_active: e.target.checked })}
                />
                <label htmlFor="is_active" style={{ marginBottom: 0 }}>Enable Account</label>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
